import { useMemo, useState } from 'react';
import { AppHeader, Toast } from '../components/common';
import { CATEGORIES } from '../data/categories';
import { validateQuestion } from '../data/questionBank';
import { useT } from '../hooks/useT';
import { makeQuestionId } from '../services/customQuestions';
import { downloadJsonFile } from '../services/storage';
import { useBank } from '../state/BankContext';
import { useSettings } from '../state/SettingsContext';
import type { CategoryId, Difficulty, Question } from '../types';

interface ChoiceDraft {
  en: string;
  ar: string;
}

const EMPTY_CHOICES: ChoiceDraft[] = [
  { en: '', ar: '' },
  { en: '', ar: '' },
  { en: '', ar: '' },
  { en: '', ar: '' },
];

/**
 * Question Manager — a small authoring page for the caregiver.
 * It is reachable only from Settings, so it never clutters the learner's screens.
 */
export function ManagerScreen() {
  const t = useT();
  const { uiLang } = useSettings();
  const { customQuestions, addCustom, removeCustom, signs } = useBank();

  const [questionEn, setQuestionEn] = useState('');
  const [questionAr, setQuestionAr] = useState('');
  const [choices, setChoices] = useState<ChoiceDraft[]>(EMPTY_CHOICES);
  const [correctIndex, setCorrectIndex] = useState(0);
  const [category, setCategory] = useState<CategoryId>('road-signs');
  const [difficulty, setDifficulty] = useState<Difficulty>('medium');
  const [imageSrc, setImageSrc] = useState('');
  const [imageAltEn, setImageAltEn] = useState('');
  const [imageAltAr, setImageAltAr] = useState('');
  const [explanationEn, setExplanationEn] = useState('');
  const [explanationAr, setExplanationAr] = useState('');
  const [source, setSource] = useState('Added locally');
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const knownImages = useMemo(() => signs.map((s) => s.image), [signs]);

  const reset = () => {
    setQuestionEn('');
    setQuestionAr('');
    setChoices(EMPTY_CHOICES.map((c) => ({ ...c })));
    setCorrectIndex(0);
    setImageSrc('');
    setImageAltEn('');
    setImageAltAr('');
    setExplanationEn('');
    setExplanationAr('');
    setError(null);
  };

  const buildDraft = (): Question | null => {
    const used = choices.filter((c) => c.en.trim() && c.ar.trim());
    const draft = {
      id: makeQuestionId(),
      category,
      questionEn: questionEn.trim(),
      questionAr: questionAr.trim(),
      answersEn: used.map((c) => c.en.trim()),
      answersAr: used.map((c) => c.ar.trim()),
      correctIndex,
      image: imageSrc.trim()
        ? { src: imageSrc.trim(), altEn: imageAltEn.trim(), altAr: imageAltAr.trim() }
        : undefined,
      explanationEn: explanationEn.trim() || undefined,
      explanationAr: explanationAr.trim() || undefined,
      difficulty,
      source: source.trim() || 'Added locally',
      isCustom: true,
    };

    const result = validateQuestion(draft, 'Question Manager');
    if (!result.ok) {
      setError(result.issue.message);
      return null;
    }
    setError(null);
    return result.value;
  };

  const handleSave = () => {
    const question = buildDraft();
    if (!question) return;
    addCustom(question);
    setToast(uiLang === 'ar' ? 'تمت إضافة السؤال.' : 'Question added.');
    reset();
  };

  return (
    <>
      <AppHeader title={t('questionManager')} />
      <main id="main" className="app-main stack">
        <div className="notice notice--info">{t('managerIntro')}</div>

        <section className="card stack">
          <h2 className="card__title">{t('addQuestion')}</h2>

          <div className="field">
            <label className="field__label" htmlFor="q-en">
              Question (English) — {t('requiredField')}
            </label>
            <textarea
              id="q-en"
              className="textarea ltr"
              value={questionEn}
              onChange={(e) => setQuestionEn(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="q-ar">
              السؤال بالعربية — {t('requiredField')}
            </label>
            <textarea
              id="q-ar"
              className="textarea"
              dir="rtl"
              value={questionAr}
              onChange={(e) => setQuestionAr(e.target.value)}
            />
          </div>

          <fieldset style={{ border: '2px solid var(--border)', borderRadius: 'var(--radius)', padding: 'var(--space-3)' }}>
            <legend style={{ fontWeight: 700 }}>{t('correctAnswerLabel')}</legend>
            {choices.map((choice, i) => (
              <div key={i} className="stack-sm" style={{ marginBottom: 'var(--space-3)' }}>
                <div className="row">
                  <input
                    type="radio"
                    id={`correct-${i}`}
                    name="correct"
                    checked={correctIndex === i}
                    onChange={() => setCorrectIndex(i)}
                    style={{ width: '1.4rem', height: '1.4rem' }}
                  />
                  <label htmlFor={`correct-${i}`} style={{ fontWeight: 700 }}>
                    {String.fromCharCode(65 + i)} — {t('correctAnswerLabel')}
                  </label>
                </div>
                <input
                  className="input ltr"
                  placeholder={`Answer ${String.fromCharCode(65 + i)} (English)`}
                  value={choice.en}
                  onChange={(e) =>
                    setChoices((prev) => prev.map((c, j) => (j === i ? { ...c, en: e.target.value } : c)))
                  }
                />
                <input
                  className="input"
                  dir="rtl"
                  placeholder={`الإجابة ${String.fromCharCode(65 + i)} بالعربية`}
                  value={choice.ar}
                  onChange={(e) =>
                    setChoices((prev) => prev.map((c, j) => (j === i ? { ...c, ar: e.target.value } : c)))
                  }
                />
              </div>
            ))}
            <div className="row">
              <button
                type="button"
                className="btn"
                onClick={() => setChoices((prev) => [...prev, { en: '', ar: '' }])}
                disabled={choices.length >= 6}
              >
                +
              </button>
              <button
                type="button"
                className="btn"
                onClick={() =>
                  setChoices((prev) => {
                    const next = prev.slice(0, Math.max(2, prev.length - 1));
                    if (correctIndex >= next.length) setCorrectIndex(next.length - 1);
                    return next;
                  })
                }
                disabled={choices.length <= 2}
              >
                −
              </button>
            </div>
          </fieldset>

          <div className="field">
            <label className="field__label" htmlFor="cat">
              {t('topics')}
            </label>
            <select
              id="cat"
              className="select"
              value={category}
              onChange={(e) => setCategory(e.target.value as CategoryId)}
            >
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.icon} {uiLang === 'ar' ? c.nameAr : c.nameEn}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="diff">
              Difficulty
            </label>
            <select
              id="diff"
              className="select"
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value as Difficulty)}
            >
              <option value="easy">easy</option>
              <option value="medium">medium</option>
              <option value="hard">hard</option>
            </select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="img">
              {t('imagePath')} — {t('optional')}
            </label>
            <input
              id="img"
              className="input ltr"
              list="known-images"
              placeholder="/question-images/signs/stop.svg"
              value={imageSrc}
              onChange={(e) => setImageSrc(e.target.value)}
            />
            <datalist id="known-images">
              {knownImages.map((src) => (
                <option key={src} value={src} />
              ))}
            </datalist>
            <span className="field__hint">
              {uiLang === 'ar'
                ? 'ضع ملف الصورة في مجلد public/question-images/ ثم اكتب مساره هنا.'
                : 'Put the image file in public/question-images/ and write its path here.'}
            </span>
          </div>

          {imageSrc ? (
            <>
              <div className="field">
                <label className="field__label" htmlFor="alt-en">
                  Image description (English)
                </label>
                <input
                  id="alt-en"
                  className="input ltr"
                  value={imageAltEn}
                  onChange={(e) => setImageAltEn(e.target.value)}
                />
              </div>
              <div className="field">
                <label className="field__label" htmlFor="alt-ar">
                  وصف الصورة بالعربية
                </label>
                <input
                  id="alt-ar"
                  className="input"
                  dir="rtl"
                  value={imageAltAr}
                  onChange={(e) => setImageAltAr(e.target.value)}
                />
              </div>
              <div className="question-image">
                <span className="question-image__button">
                  <img src={imageSrc} alt={imageAltEn || 'preview'} />
                </span>
              </div>
            </>
          ) : null}

          <div className="field">
            <label className="field__label" htmlFor="exp-en">
              Explanation (English) — {t('optional')}
            </label>
            <textarea
              id="exp-en"
              className="textarea ltr"
              value={explanationEn}
              onChange={(e) => setExplanationEn(e.target.value)}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="exp-ar">
              الشرح بالعربية — {t('optional')}
            </label>
            <textarea
              id="exp-ar"
              className="textarea"
              dir="rtl"
              value={explanationAr}
              onChange={(e) => setExplanationAr(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="src">
              Source
            </label>
            <input id="src" className="input ltr" value={source} onChange={(e) => setSource(e.target.value)} />
          </div>

          {error ? <div className="notice notice--danger">{error}</div> : null}

          <div className="row">
            <button type="button" className="btn btn--primary btn--big" onClick={handleSave}>
              {t('save')}
            </button>
            <button type="button" className="btn" onClick={reset}>
              {t('cancel')}
            </button>
          </div>
        </section>

        <section className="card stack-sm">
          <h2 className="card__title">
            {t('customQuestions')} ({customQuestions.length})
          </h2>

          {customQuestions.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--text-muted)' }}>{t('noData')}</p>
          ) : (
            <>
              {customQuestions.map((q) => (
                <div key={q.id} className="notice">
                  <div style={{ fontWeight: 700 }}>{uiLang === 'ar' ? q.questionAr : q.questionEn}</div>
                  <div className="ltr" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {q.id} · {q.category}
                  </div>
                  <button
                    type="button"
                    className="btn btn--danger"
                    style={{ marginTop: 'var(--space-2)' }}
                    onClick={() => removeCustom(q.id)}
                  >
                    {t('deleteLabel')}
                  </button>
                </div>
              ))}

              <div className="row">
                <button
                  type="button"
                  className="btn"
                  onClick={() =>
                    downloadJsonFile('my-questions.json', {
                      questions: customQuestions.map(({ isCustom, ...rest }) => rest),
                    })
                  }
                >
                  <span className="btn__icon" aria-hidden="true">
                    💾
                  </span>
                  {t('downloadJson')}
                </button>
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    void navigator.clipboard
                      ?.writeText(
                        JSON.stringify(
                          { questions: customQuestions.map(({ isCustom, ...rest }) => rest) },
                          null,
                          2,
                        ),
                      )
                      .then(() => setToast(t('copied')));
                  }}
                >
                  {t('copyJson')}
                </button>
              </div>

              <p className="field__hint">
                {uiLang === 'ar'
                  ? 'لجعل هذه الأسئلة دائمة، ضع الملف الذي نزّلته في مجلد src/data/questions/.'
                  : 'To make these permanent, put the downloaded file into src/data/questions/.'}
              </p>
            </>
          )}
        </section>
      </main>

      {toast ? <Toast message={toast} onDone={() => setToast(null)} /> : null}
    </>
  );
}
